import React from 'react';
import { TemplateFrame } from '../TemplateFrame';
import { Heart, Mic, Link as LinkIcon, Clock } from 'lucide-react';
const FN_ICON = 'https://cdn.magicpatterns.com/uploads/6S42RJUThA4LVcV5MgRYxz/filmnotes-fn-full-icon_1024.png';
// Safe zones (top/bottom 13% — ~250px on a 1920 tall)
function StorySafeZones() {
  return <>
      <div className="absolute top-0 left-0 right-0 pointer-events-none border-b border-dashed border-film-red/40" style={{
      height: '13%'
    }} data-id="element-1520">
        <div className="w-full h-full bg-film-red/[0.04] flex items-center justify-center" data-id="element-1521">
          <span className="font-mono text-[7px] text-film-red/70 uppercase tracking-wider" data-id="element-1522">
            Sticker-safe top
          </span>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 pointer-events-none border-t border-dashed border-film-red/40" style={{
      height: '13%'
    }} data-id="element-1523">
        <div className="w-full h-full bg-film-red/[0.04] flex items-center justify-center" data-id="element-1524">
          <span className="font-mono text-[7px] text-film-red/70 uppercase tracking-wider" data-id="element-1525">
            Sticker-safe bottom
          </span>
        </div>
      </div>
    </>;
}
// Helper: inner content sits between safe zones
function StoryInner({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`absolute inset-0 flex flex-col px-4 ${className}`} style={{
    paddingTop: '15%',
    paddingBottom: '15%'
  }} data-id="element-1526">
      {children}
    </div>;
}
function SectionHeader({
  id,
  title,
  sub
}: {
  id: string;
  title: string;
  sub: string;
}) {
  return <div className="mb-8" data-id="element-1527">
      <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray mb-1" data-id="element-1528">
        {id}
      </p>
      <h2 className="font-display font-bold text-2xl text-film-black" data-id="element-1529">
        {title}
      </h2>
      <p className="text-sm text-film-gray mt-1 max-w-xl" data-id="element-1530">{sub}</p>
    </div>;
}
export function StoriesTemplatesPage() {
  return <div className="max-w-6xl mx-auto py-12 px-8 space-y-20" data-id="element-1531">
      {/* Header */}
      <div data-id="element-1532">
        <p className="font-mono text-xs text-film-gray mb-3" data-id="element-1533">
          03_TEMPLATES_STORIES
        </p>
        <h1 className="font-display font-bold text-4xl text-film-black mb-2" data-id="element-1534">
          Story Templates
        </h1>
        <p className="text-film-gray max-w-2xl" data-id="element-1535">
          9:16 · 1080×1920 · Day-on / day-off rhythm. 2–5 frames per on-day. All
          content stays clear of the top and bottom 13% sticker-safe zones.
        </p>
      </div>

      {/* Story Mix Reminder */}
      <div className="bg-white border border-film-border p-5 shadow-card" data-id="element-1536">
        <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray mb-3" data-id="element-1537">
          Story mix · target ratios
        </p>
        <div className="flex h-7 overflow-hidden border border-film-border" data-id="element-1538">
          <div className="bg-film-red text-white text-[10px] flex items-center justify-center font-mono" style={{
          width: '40%'
        }} data-id="element-1539">
            40% RESHARE
          </div>
          <div className="bg-film-orange text-white text-[10px] flex items-center justify-center font-mono" style={{
          width: '30%'
        }} data-id="element-1540">
            30% INTERACTIVE
          </div>
          <div className="bg-film-black text-white text-[10px] flex items-center justify-center font-mono" style={{
          width: '20%'
        }} data-id="element-1541">
            20% BTS
          </div>
          <div className="bg-film-gray text-white text-[10px] flex items-center justify-center font-mono" style={{
          width: '10%'
        }} data-id="element-1542">
            10% UGC
          </div>
        </div>
        <p className="font-mono text-[9px] text-film-gray mt-2 leading-snug" data-id="element-1543">
          Reshare = feed echo · Interactive = poll / quiz / question · BTS =
          behind the scenes · UGC = community repost
        </p>
      </div>

      {/* ===================== VARIANTS ===================== */}
      <section data-id="element-1544">
        <SectionHeader id="§ VARIANTS" title="Story Variants" sub="Six base layouts. Mix and match across an on-day stack. Frames are sized so the centre band is always reachable by a thumb." data-id="element-1545" />
        <div className="flex flex-wrap gap-7" data-id="element-1546">
          {/* 01 · Reshare + Poll (fixed: clean vertical stack, no overlap) */}
          <TemplateFrame componentId="FMT_STORY · Reshare + Poll" dimensions="1080×1920 · 9:16" caption="Default after every feed drop. Poll sits below the reshare card with comfortable breathing room." width="220px" data-id="element-1547">
            <div className="relative bg-film-cream w-full" style={{
            aspectRatio: '9/16'
          }} data-id="element-1548">
              <StorySafeZones data-id="element-1549" />
              <StoryInner data-id="element-1550">
                {/* Reshare card */}
                <div className="bg-white border border-film-border w-full aspect-square shadow-sm flex flex-col items-center justify-center mt-2" data-id="element-1551">
                  <span className="font-mono text-[8px] text-film-gray uppercase tracking-wider mb-1" data-id="element-1552">
                    Feed reshare
                  </span>
                  <span className="font-display font-bold text-film-black text-sm" data-id="element-1553">
                    Push or pull?
                  </span>
                </div>

                {/* Poll widget */}
                <div className="bg-white border border-film-border shadow-sm rounded-lg w-full px-3 py-2.5 mt-4" data-id="element-1554">
                  <p className="font-display font-bold text-[11px] text-film-black text-center mb-2" data-id="element-1555">
                    Which do you shoot?
                  </p>
                  <div className="grid grid-cols-2 gap-1.5" data-id="element-1556">
                    <div className="bg-film-cream border border-film-border text-[9px] font-mono py-1.5 text-center text-film-black uppercase tracking-wider" data-id="element-1557">
                      Push&nbsp;+1
                    </div>
                    <div className="bg-film-cream border border-film-border text-[9px] font-mono py-1.5 text-center text-film-black uppercase tracking-wider" data-id="element-1558">
                      Pull&nbsp;-1
                    </div>
                  </div>
                </div>
              </StoryInner>
            </div>
          </TemplateFrame>

          {/* 02 · Interactive question (black) */}
          <TemplateFrame componentId="FMT_STORY · Question" dimensions="1080×1920 · 9:16" caption="Open question prompt. Use sparingly — once per on-day max." width="220px" data-id="element-1559">
            <div className="relative bg-film-black w-full" style={{
            aspectRatio: '9/16'
          }} data-id="element-1560">
              <StorySafeZones data-id="element-1561" />
              <StoryInner className="justify-center text-center" data-id="element-1562">
                <p className="font-mono text-[8px] uppercase tracking-wider text-white/40 mb-3" data-id="element-1563">
                  Question
                </p>
                <h3 className="font-display font-bold text-white text-xl leading-[1.05] mb-6" data-id="element-1564">
                  Which film did
                  <br data-id="element-1565" />
                  you shoot last?
                </h3>
                <div className="bg-film-cream w-full px-3 py-2.5 rounded-lg" data-id="element-1566">
                  <p className="font-mono text-[9px] text-film-gray italic" data-id="element-1567">
                    type to reply…
                  </p>
                </div>
              </StoryInner>
            </div>
          </TemplateFrame>

          {/* 03 · BTS Photo + voice note */}
          <TemplateFrame componentId="FMT_STORY · BTS Photo" dimensions="1080×1920 · 9:16" caption="Chill, voice-note tone. Photo + 4 words + audio chip." width="220px" data-id="element-1568">
            <div className="relative w-full bg-film-black" style={{
            aspectRatio: '9/16'
          }} data-id="element-1569">
              <img src="https://images.unsplash.com/photo-1606925797300-0b35e9d1794e?w=600&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" data-id="element-1570" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" data-id="element-1571" />
              <StorySafeZones data-id="element-1572" />
              <StoryInner className="justify-end" data-id="element-1573">
                <div className="bg-white/95 backdrop-blur px-3 py-2 rounded inline-block self-start max-w-[80%]" data-id="element-1574">
                  <p className="font-display font-bold text-sm text-film-black leading-tight" data-id="element-1575">
                    loaded HP5 today
                  </p>
                </div>
                <div className="flex items-center gap-1.5 mt-2.5 self-start bg-black/40 backdrop-blur px-2.5 py-1 rounded" data-id="element-1576">
                  <Mic className="w-3 h-3 text-white" data-id="element-1577" />
                  <span className="font-mono text-[8px] text-white uppercase tracking-wider" data-id="element-1578">
                    Voice · 0:14
                  </span>
                </div>
              </StoryInner>
            </div>
          </TemplateFrame>

          {/* 04 · UGC Thank-you (cream) */}
          <TemplateFrame componentId="FMT_STORY · UGC Thank-you" dimensions="1080×1920 · 9:16" caption="Reposted tag with credit + thank-you ribbon." width="220px" data-id="element-1579">
            <div className="relative bg-film-cream w-full" style={{
            aspectRatio: '9/16'
          }} data-id="element-1580">
              <StorySafeZones data-id="element-1581" />
              <StoryInner className="items-center" data-id="element-1582">
                <p className="font-mono text-[8px] uppercase tracking-wider text-film-gray mb-3 text-center" data-id="element-1583">
                  @sarah_analog tagged us
                </p>
                <div className="w-[80%] aspect-[4/5] overflow-hidden border border-film-border bg-white" data-id="element-1584">
                  <img src="https://images.unsplash.com/photo-1514539079130-25950c84af65?w=400&q=80" className="w-full h-full object-cover" alt="" data-id="element-1585" />
                </div>
                <div className="mt-4 text-white px-3 py-1.5 inline-block" style={{
                backgroundColor: '#5B3A82'
              }} data-id="element-1586">
                  <p className="font-display font-bold text-xs leading-tight" data-id="element-1587">
                    thanks for tagging us 🙏
                  </p>
                </div>
              </StoryInner>
            </div>
          </TemplateFrame>

          {/* 05 · NEW · Countdown (black) */}
          <TemplateFrame componentId="FMT_STORY · Countdown" dimensions="1080×1920 · 9:16" caption="Pre-launch beat. Monospace tabular countdown." width="220px" data-id="element-1588">
            <div className="relative bg-film-black w-full" style={{
            aspectRatio: '9/16'
          }} data-id="element-1589">
              <StorySafeZones data-id="element-1590" />
              <StoryInner className="justify-center items-center text-center" data-id="element-1591">
                <img src={FN_ICON} className="w-7 h-7 mb-4 brightness-0 invert opacity-90" alt="" data-id="element-1592" />
                <p className="font-mono text-[8px] uppercase tracking-wider text-white/40 mb-2" data-id="element-1593">
                  New in app
                </p>
                <div className="flex items-baseline gap-1 font-mono tabular-nums text-white text-3xl font-bold" data-id="element-1594">
                  <span data-id="element-1595">02</span>
                  <span className="text-white/40" data-id="element-1596">:</span>
                  <span data-id="element-1597">14</span>
                  <span className="text-white/40" data-id="element-1598">:</span>
                  <span data-id="element-1599">36</span>
                </div>
                <div className="flex items-baseline gap-2 font-mono text-[7px] text-white/40 mt-1 uppercase tracking-widest" data-id="element-1600">
                  <span data-id="element-1601">days</span>
                  <span data-id="element-1602">hrs</span>
                  <span data-id="element-1603">min</span>
                </div>
                <div className="mt-5 px-3 py-1 text-white font-mono text-[9px] uppercase tracking-wider" style={{
                backgroundColor: '#D9711F'
              }} data-id="element-1604">
                  Tap reminder
                </div>
              </StoryInner>
            </div>
          </TemplateFrame>

          {/* 06 · NEW · Link sticker CTA */}
          <TemplateFrame componentId="FMT_STORY · Link CTA" dimensions="1080×1920 · 9:16" caption="App screenshot mock + link sticker pinned in the bottom safe-line." width="220px" data-id="element-1605">
            <div className="relative bg-film-cream w-full" style={{
            aspectRatio: '9/16'
          }} data-id="element-1606">
              <StorySafeZones data-id="element-1607" />
              <StoryInner className="justify-between items-center" data-id="element-1608">
                <p className="font-mono text-[8px] uppercase tracking-wider text-film-gray text-center mt-1" data-id="element-1609">
                  Now in beta
                </p>

                {/* Phone mock */}
                <div className="relative w-[68%] aspect-[9/16] bg-white rounded-[14px] border border-film-border shadow-sm overflow-hidden" data-id="element-1610">
                  <div className="h-6 flex items-center px-2 gap-1.5" style={{
                  backgroundColor: '#5B3A82'
                }} data-id="element-1611">
                    <img src={FN_ICON} className="w-3 h-3 brightness-0 invert" alt="" data-id="element-1612" />
                    <span className="font-mono text-[7px] text-white uppercase tracking-wider" data-id="element-1613">
                      Film Notes
                    </span>
                  </div>
                  <div className="p-2 space-y-1.5" data-id="element-1614">
                    <div className="bg-film-cream border border-film-border p-1.5" data-id="element-1615">
                      <div className="flex items-center gap-1" data-id="element-1616">
                        <div className="w-1.5 h-1.5 rounded-full bg-film-red" data-id="element-1617" />
                        <div className="h-1 flex-1 bg-film-black/10 rounded-sm" data-id="element-1618" />
                      </div>
                      <div className="h-1 w-3/4 bg-film-black/5 rounded-sm mt-1" data-id="element-1619" />
                    </div>
                    <div className="bg-film-cream border border-film-border p-1.5" data-id="element-1620">
                      <div className="flex items-center gap-1" data-id="element-1621">
                        <div className="w-1.5 h-1.5 rounded-full" style={{
                        backgroundColor: '#2D7A4F'
                      }} data-id="element-1622" />
                        <div className="h-1 flex-1 bg-film-black/10 rounded-sm" data-id="element-1623" />
                      </div>
                      <div className="h-1 w-2/3 bg-film-black/5 rounded-sm mt-1" data-id="element-1624" />
                    </div>
                  </div>
                </div>

                {/* Link sticker */}
                <div className="bg-white border border-film-border rounded-full px-3 py-1.5 flex items-center gap-1.5 shadow-sm" data-id="element-1625">
                  <LinkIcon className="w-3 h-3 text-film-red" data-id="element-1626" />
                  <span className="font-display font-bold text-[10px] text-film-black uppercase tracking-wider" data-id="element-1627">
                    Tap to install
                  </span>
                </div>
              </StoryInner>
            </div>
          </TemplateFrame>
        </div>
      </section>

      {/* ===================== 3-FRAME SEQUENCE ===================== */}
      <section data-id="element-1628">
        <SectionHeader id="§ STORY_SEQUENCE" title="3-frame sequence" sub="A complete on-day stack: reshare → poll → CTA. Connect with subtle arrow between frames." data-id="element-1629" />
        <div className="flex flex-wrap gap-3 items-start" data-id="element-1630">
          {/* Frame 1 · Reshare */}
          <div className="flex items-center gap-3" data-id="element-1631">
            <TemplateFrame componentId="STORY_SEQ_01 · Reshare" dimensions="1080×1920 · 9:16" width="200px" data-id="element-1632">
              <div className="relative bg-film-cream w-full" style={{
              aspectRatio: '9/16'
            }} data-id="element-1633">
                <StorySafeZones data-id="element-1634" />
                <StoryInner className="justify-center" data-id="element-1635">
                  <div className="bg-white border border-film-border w-full aspect-square shadow-sm flex flex-col items-center justify-center" data-id="element-1636">
                    <span className="font-mono text-[8px] text-film-gray uppercase tracking-wider mb-1" data-id="element-1637">
                      Feed post
                    </span>
                    <span className="font-display font-bold text-film-black text-sm" data-id="element-1638">
                      May drop
                    </span>
                  </div>
                  <p className="font-mono text-[8px] text-film-gray text-center mt-3 uppercase tracking-wider" data-id="element-1639">
                    Tap to view →
                  </p>
                </StoryInner>
              </div>
            </TemplateFrame>
            <div className="font-mono text-2xl text-film-gray/60 pt-12" data-id="element-1640">→</div>
          </div>

          {/* Frame 2 · Poll */}
          <div className="flex items-center gap-3" data-id="element-1641">
            <TemplateFrame componentId="STORY_SEQ_02 · Poll" dimensions="1080×1920 · 9:16" width="200px" data-id="element-1642">
              <div className="relative bg-film-black w-full" style={{
              aspectRatio: '9/16'
            }} data-id="element-1643">
                <StorySafeZones data-id="element-1644" />
                <StoryInner className="justify-center text-center" data-id="element-1645">
                  <h3 className="font-display font-bold text-white text-base leading-[1.05] mb-5" data-id="element-1646">
                    Which slide
                    <br data-id="element-1647" />
                    deserves a deep-dive?
                  </h3>
                  <div className="bg-white text-film-black w-full px-3 py-2 rounded text-[10px] font-mono mb-1.5 text-left" data-id="element-1648">
                    Slide 3 · Push/pull
                  </div>
                  <div className="bg-white text-film-black w-full px-3 py-2 rounded text-[10px] font-mono text-left" data-id="element-1649">
                    Slide 5 · Lab map
                  </div>
                </StoryInner>
              </div>
            </TemplateFrame>
            <div className="font-mono text-2xl text-film-gray/60 pt-12" data-id="element-1650">→</div>
          </div>

          {/* Frame 3 · CTA */}
          <TemplateFrame componentId="STORY_SEQ_03 · CTA" dimensions="1080×1920 · 9:16" width="200px" data-id="element-1651">
            <div className="relative bg-film-cream w-full" style={{
            aspectRatio: '9/16'
          }} data-id="element-1652">
              <div className="absolute left-0 top-0 bottom-0 bg-film-red" style={{
              width: '4px'
            }} data-id="element-1653" />
              <StorySafeZones data-id="element-1654" />
              <StoryInner className="justify-center items-center text-center" data-id="element-1655">
                <img src={FN_ICON} className="w-9 h-9 mb-3" alt="" data-id="element-1656" />
                <p className="font-display font-bold text-film-black text-base leading-tight mb-3" data-id="element-1657">
                  Get
                  <br data-id="element-1658" />
                  Film Notes.
                </p>
                <div className="bg-film-red text-white text-[9px] font-mono px-3 py-1.5 uppercase tracking-wider" data-id="element-1659">
                  App Store
                </div>
              </StoryInner>
            </div>
          </TemplateFrame>
        </div>
      </section>
    </div>;
}